import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Container } from '../../containers/container';
import { Group } from '../../entities/group/group';
import { Location } from '../../entities/location/location';
import { Role } from '../../entities/role/role';
import { User } from '../../entities/user/user';
import { GrowthBookService } from '../../services/growthbook.service';
import { LoadingService } from '../../services/loading.service';
import { LocationService } from '../../services/location/location.service';
import { RolesService } from '../../services/roles/roles.service';
import { ToastService } from '../../services/toast/toast.service';
import { UsersService } from '../../services/users/users.service';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { EditDialogService } from '../editDialog/editDialogService';
import { UserPreferredLanguageDialogComponent } from '../user-preferred-language-dialog/user-preferred-language-dialog.component';
import * as fromGraphQl from './graphql';
import { PROFILE_TAB_QUERY_PARAM, profileTabIndexFor } from './profile-tabs';


interface Setting {
  key: string;
  value: any;
}

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent
  extends Container<fromGraphQl.UserQueryResponse>
  implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public familyName: string;
  public givenName: string;

  public user: User;
  public id: string;
  public routeSubscription: Subscription;
  public UserClass = User;
  public locations: Location[];
  public availableRoles: Role[];
  public usersRoles: Role[];
  public usersRolesFiltered: Role[];

  public userGroups: Group[];
  public selectedRoles: Role[];

  /**
   * Aktiver Tab. Wird aus ?tab= gesetzt (#735) und danach NICHT mehr angefasst: die Bindung ist
   * einwegig, ein Tab-Wechsel des Nutzers laeuft rein in p-tabView und schreibt hier nichts
   * zurueck — sonst zoege die Bindung ihn wieder auf den Eingangstab.
   */
  public activeTabIndex = 0;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    loadingService: LoadingService,
    apollo: Apollo,
    public dialogService: EditDialogService,
    private confirmDialogService: ConfirmDialogService,
    @Inject('env') environment : any,
    private userService: UsersService,
    private toastService: ToastService,
    private locationService: LocationService,
    private rolesService: RolesService,
    private keycloakAngular: KeycloakService,
    private growthBookService: GrowthBookService
  ) {
    super(apollo, loadingService, fromGraphQl.USERS_QUERY, environment);
  }

  ngOnInit(): void {
    // Einstieg aus einer Benachrichtigungsmail: /profile?tab=notifications (#735).
    // In ngOnInit gesetzt zu werden reicht — TabView.initTabs() laeuft erst in
    // ngAfterContentInit und uebernimmt ein bis dahin gesetztes activeIndex.
    // Ueber queryParamMap statt snapshot, damit eine Navigation auf denselben Screen mit anderem
    // Parameter greift; Angular verwendet die Komponenteninstanz dabei wieder.
    this.routeSubscription = this.route.queryParamMap.subscribe((params) => {
      this.activeTabIndex = profileTabIndexFor(params.get(PROFILE_TAB_QUERY_PARAM));
    });

    this.familyName = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['family_name']
      : '';
    this.givenName = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['given_name']
      : '';
    this.id = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['id']
      : '';
    this.subscribeToQuery({
      id: this.id,
    });
    this.locationService.getLocations().pipe(take(1)).subscribe((locations) => {
      this.locations = locations;
    });
    this.rolesService.getRoles().pipe(take(1)).subscribe((roles) => {
      this.availableRoles = roles;
      this.filterUserRoles();
      this.filterUserGroups();
    });
  }

  getLabel(): string{
    return this.givenName + ' ' + this.familyName;
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  handleSubscriptionResult(data: fromGraphQl.UserQueryResponse): void {
    this.loadingService.setLoading(false);
    if (data) {
      this.queryRef.stopPolling();
      if (data.user) {
        this.user = new User(data.user);
        this.filterUserRoles();
      }
    }
  }

  getUserLocations(): string {
    // get the location ids of the user an map them to the location names
    // if it cannot find locations, skip them

    let assignedLocations = this.user?.locationIds?.map((locationId) => {
      const location = this.locations.find((location) => location.id === locationId);
      return location?.name;
    });
    // filter all undefined values from array
    return assignedLocations?.filter((location) => location !== undefined).join(', ');
  }

  filterUserRoles(): void {
    if (this.user && this.availableRoles) {
      this.usersRoles = this.user.roles.filter((role) => {
        return this.availableRoles.find((availableRole) => availableRole.id === role.id);
      });
      this.usersRolesFiltered = this.usersRoles;
    }
  }

  filterUserGroups(): void {
    // we want to filter out the tenant group
    let tenantId = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['tenant_id']
      : '';
    if (this.user && this.user.groups) {
      this.userGroups = this.user.groups.filter((group) => {
        return group.name !== tenantId;
      });
    }
  }

  loadData(event: TableLazyLoadEvent) {
    //filtering
    let normalizedSearchTerm = (event.globalFilter as string)?.toLowerCase();

    // pagination
    if (normalizedSearchTerm) {
      this.usersRolesFiltered = this.usersRoles.filter((role) =>
        role.label?.toLowerCase().includes(normalizedSearchTerm)
      );
    }
    else {
      this.usersRolesFiltered = this.usersRoles;
    }
  }

  editPreferredLanguage() {
    this.dialogRef = this.dialogService.open(UserPreferredLanguageDialogComponent, {
      header: 'core.user.preferredLanguageDialog.header',
      data: this.user
    });
    this.dialogRef.onClose.subscribe((result) => {
      if(result?.preferredLanguage) {
        // #735: Toast erst nach Server-Bestaetigung — der Erfolgsfall darf nicht mehr
        // bedingungslos gemeldet werden.
        this.userService.updatePreferredLanguage(result.preferredLanguage).subscribe({
          next: () => {
            // Anzeigequelle ist der Backend-Wert, die Query muss also nachziehen.
            // catch: der Erfolgs-Toast ist bereits raus, ein Refetch-Fehler darf keine
            // Unhandled Promise Rejection werden.
            this.queryRef?.refetch()?.catch(() => undefined);
            this.toastService.addToastWithMessage('core.user.preferredLanguageDialog.successToastMessage');
          },
          error: () => {
            this.toastService.addErrorToast('core.user.preferredLanguageDialog.errorToastMessage');
          }
        });
      }
    });
  }

  /**
   * Anzeigequelle ist seit #735 der Backend-Wert, NICHT mehr der localStorage-Cache.
   */
  getPreferredLanguage(): string {
    const preferredLanguage = this.user?.preferredLanguage;
    return preferredLanguage ? 'core.common.language.' + preferredLanguage : '-';
  }

}
